import { max, min_value } from '@vee-validate/rules';
import { defineRule } from 'vee-validate';

defineRule('unique-course-name', (value, { isDuplicateCourseName, rowData }) => {
    if (!isDuplicateCourseName(value, rowData)) return true;
    return `Course name cannot be repeated within this campus/department.`;
});

defineRule('unique-campus-abbreviation', (value, { isDuplicateCampusAbbreviation, rowData }) => {
    if (!isDuplicateCampusAbbreviation(value, rowData)) return true;
    return `Abbreviation is used for a different campus.`;
});

defineRule('consistent-department-description', (value, { hasConflictingDepartmentDescription, rowData }) => {
    if (!hasConflictingDepartmentDescription(value, rowData)) return true;
    return `Every row for this department and campus must give the same description.`;
});

defineRule('consistent-department-status', (value, { hasConflictingDepartmentStatus, rowData }) => {
    if (!hasConflictingDepartmentStatus(value, rowData)) return true;
    return `Every row for this department and campus must give the same active status.`;
});

export default {
    data() {
        return {
            courseConfiguration: {
                type: 'courses',
                label: 'Courses with Campuses and Departments',
                sampleNotes: [
                    '"The imported course will be matched with an existing department and campus if it they can be found with the same name."',
                    '"If a department/campus is not found by the provided name, a new department/campus will be created with the provided details."',
                    '"Existing departments/campuses will not be updated by the provided details."',
                    '"A department name may be reused on another campus; it identifies a different department there."',
                    '"Rows for the same department and campus must repeat the same department description and active status."',
                ],
                importNotes: () => {return this.courseImportNotes},
                fields: [
                    {
                        //course is unique within the department.
                        key: 'CourseName',
                        label: 'Course Name',
                        canConvertCase: true,
                        hasErrorCalculator: (value, row, checkOriginalData)  => {
                            return (
                                value === '' ||
                                max(value, { length: 80 }) !== true
                                || this.isDuplicateCourseName(value, row, checkOriginalData)
                            );
                        },

                        rules: {
                            'unique-course-name': {
                                isDuplicateCourseName: this
                                    .isDuplicateCourseName
                            },
                            required: true,
                            max: { length: 80 }
                        },
                        addRowDataToRule: 'unique-course-name',
                        samples: [
                            '"303"',
                            '"304"',
                            '"430"'
                        ]
                    },
                    {
                        key: 'CourseDescription',
                        label: 'Course Description',
                        canConvertCase: true,
                        hasErrorCalculator: value => {
                            return (
                                value === '' ||
                                max(value, { length: 255 }) !== true
                            );
                        },
                        rules: 'required|max:255',
                        samples: [
                            '"Computers in Arts & Science"',
                            '"Computers in Arts & Science"',
                            '"Computers in Arts & Science"'
                        ]
                    },
                    {
                        key: 'CourseComments',
                        label: 'Course Comments',
                        canConvertCase: true,
                        hasErrorCalculator: value => {
                            return (
                                max(value, { length: 255 }) !== true
                            );
                        },
                        rules: 'max:255',
                        samples: [
                            '"Comment on course 303"',
                            '"Comment on course 304"',
                            '"Comment on course 430"'
                        ]
                    },

                    //a department is identified by name + campus, so the same name may repeat
                    //freely: on other campuses it is a different department, and on the same
                    //campus it is the same department shared by several courses. That leaves
                    //no in-file uniqueness conflict to detect here.
                    {
                        key: 'DepartmentName',
                        label: 'Department Name',
                        canConvertCase: true,
                        hasErrorCalculator: value => {
                            return (
                                value === '' ||
                                max(value, { length: 80 }) !== true
                            );
                        },
                        rules: {
                            required: true,
                            max: { length: 80 }
                        },
                        samples: [
                            '"Computer Science"',
                            '"Computer Science"',
                            '"Computer Science"'
                        ]
                    },
                    {
                        key: 'DepartmentDescription',
                        label: 'Department Description',
                        hasErrorCalculator: (value, row) => {
                            return (
                                value === '' ||
                                max(value, { length: 255 }) !== true
                                || this.hasConflictingDepartmentDescription(value, row)
                                );
                        },
                        rules: {
                            'consistent-department-description': {
                                hasConflictingDepartmentDescription: this
                                    .hasConflictingDepartmentDescription
                            },
                            required: true,
                            max: { length: 255 }
                        },
                        addRowDataToRule: 'consistent-department-description',
                        samples: [
                            '"Description for Computer Science"',
                            '"Description for Computer Science"',
                            '"Description for Computer Science"'
                        ]
                    },

                    {
                        key: 'DepartmentIsActive',
                        label: 'Department is Active',
                        hasWarningCalculator: value => {
                            return this.isUnmatchedYesNo(value);
                        },
                        hasErrorCalculator: (value, row) => {
                            return this.hasConflictingDepartmentStatus(value, row);
                        },
                        dataType: 'yesNoOptions',
                        warningDefault: true,
                        rules: {
                            'consistent-department-status': {
                                hasConflictingDepartmentStatus: this
                                    .hasConflictingDepartmentStatus
                            },
                            min_value: { min: 0 }
                        },
                        addRowDataToRule: 'consistent-department-status',
                        samples: [
                            '"Yes"',
                            '"Yes"',
                            '"Yes"'
                        ]
                    },

                    //Campus name is unique
                    {
                        key: 'CampusName',
                        label: 'Campus Name',
                        hasErrorCalculator: value => {
                            return (
                                this.isEmpty(value) ||
                                max(value, { length: 80 }) !== true
                            );
                        },
                        rules: 'required|max:80',
                        samples: [
                            '"Greenville University"',
                            '"Greenville University"',
                            '"Greenville University"'
                        ]
                    },
                    {
                        key: 'CampusDescription',
                        label: 'Campus Description',
                        hasErrorCalculator: value => {
                            return (
                                this.isEmpty(value) || max(value, { length: 255 }) !== true
                            );
                        },
                        rules: 'required|max:255',
                        samples: [
                            '"Description for Greenville University"',
                            '"Description for Greenville University"',
                            '"Description for Greenville University"'
                        ]
                    },

                    // abbreviation is unique //unique-campus-abbreviation
                    {
                        key: 'CampusAbbreviation',
                        label: 'Campus Abbreviation',
                        canConvertCase: true,
                        hasErrorCalculator: (value, row, checkOriginalData)  => {
                            return (
                                value === '' ||
                                max(value, { length: 40 }) !== true
                                || this.isDuplicateCampusAbbreviation(value, row, checkOriginalData)
                            );
                        },
                        rules: {
                            'unique-campus-abbreviation': {
                                isDuplicateCampusAbbreviation: this
                                    .isDuplicateCampusAbbreviation
                            },
                            required: true,
                            max: { length: 40 }
                        },
                        addRowDataToRule: 'unique-campus-abbreviation',
                        samples: [
                            '"GRE"',
                            '"GRE"',
                            '"GRE"'
                        ]
                    },
                    {
                        key: 'CampusIsActive',
                        label: 'Campus Is Active',
                        hasWarningCalculator: value => {
                            return (
                                value != '' &&
                                value != undefined &&
                                min_value(value, { min: 0 }) !== true
                            );
                        },
                        dataType: 'yesNoOptions',
                        warningDefault: true,
                        rules: 'min_value:0',
                        samples: [
                            '"Yes"',
                            '"Yes"',
                            '"Yes"'
                        ]
                    },
                ]
            }
        };
    },

    computed:{
        courseImportNotes(){
            let notes = []

            if(this.newDepartments.length > 0){
                let departments = this.newDepartments.map(x => x.DepartmentName + '/' + x.CampusName).filter((v, i, a) => a.indexOf(v) === i)
                notes.push(`The following ${departments.length} new department(s) will be created: ${departments.join(', ')}`  )
            }

            if(this.newCampuses.length > 0){
                let campuses = this.newCampuses.map(x => x.CampusName).filter((v, i, a) => a.indexOf(v) === i)
                notes.push(`The following ${campuses.length} new campus(es) will be created: ${campuses.join(', ')}`)
            }
            return notes;
        },

        newDepartments(){
            return this.loadedData.filter(x => this.isNewDepartment(x))
        },
        newCampuses(){
            return this.loadedData.filter(x =>
                !this.selectListOptions.campuses.some(s => s.text === x.CampusName))
        }
    },
    methods: {
        //a department the file will create, rather than one already on the campus. Existing
        //departments are never updated by an import, so their details are not worth comparing.
        isNewDepartment(rowData){
            return !this.selectListOptions
                .courseDepartments
                .some(x => x.campusName?.toLowerCase() === rowData.CampusName?.toLowerCase()
                    && x.text?.toLowerCase() === rowData.DepartmentName?.toLowerCase())
        },

        rowsForSameDepartment(rowData){
            return this.loadedData
                .filter(x => x.CampusName?.toLowerCase() === rowData.CampusName?.toLowerCase()
                    && x.DepartmentName?.toLowerCase() === rowData.DepartmentName?.toLowerCase())
        },

        //every row naming the same department on the same campus describes one department, and
        //the first of them supplies the details it is created with, so a row that disagrees
        //would be silently dropped.
        hasConflictingDepartmentDescription(departmentDescription, rowData){
            if (this.isEmpty(departmentDescription) || !this.isNewDepartment(rowData)) {
                return false;
            }

            return this.rowsForSameDepartment(rowData)
                .some(x => !this.isEmpty(x.DepartmentDescription)
                    && x.DepartmentDescription?.toLowerCase() !== departmentDescription.toLowerCase())
        },

        hasConflictingDepartmentStatus(departmentIsActiveId, rowData){
            if (!this.isNewDepartment(rowData)) {
                return false;
            }

            const isActive = this.departmentIsActiveForImport(departmentIsActiveId);

            return this.rowsForSameDepartment(rowData)
                .some(x => this.departmentIsActiveForImport(x.DepartmentIsActiveId) !== isActive)
        },

        //a blank or unmatched status is replaced with the warning default before the rows are
        //posted, so compare the value the department will actually be created with.
        departmentIsActiveForImport(departmentIsActiveId){
            return this.isEmpty(departmentIsActiveId) || this.isUnmatchedYesNo(departmentIsActiveId)
                ? true
                : departmentIsActiveId
        },

        isUnmatchedYesNo(value){
            return value != ''
                && value != undefined
                && min_value(value, { min: 0 }) !== true
        },

        isDuplicateCourseName(courseName, rowData, checkOriginalData) {
            return this.isExistingCourseName(courseName, rowData)
                    || this.isDuplicatedInImportDataCourseName(courseName, rowData, checkOriginalData);

        },

        isExistingCourseName(courseName, rowData){
            return courseName != ''
                && this.selectListOptions
                    .courses
                    .some(
                            x => x.text?.toLowerCase() === courseName.toLowerCase()
                            && x.departmentName?.toLowerCase() === rowData.DepartmentName?.toLowerCase()
                            && x.campusName?.toLowerCase() === rowData.CampusName?.toLowerCase()
                         )
        },

        isDuplicatedInImportDataCourseName(courseName, rowData, checkOriginalData){
            return courseName != ''
                && this.loadedData
                    .filter(x => (
                                    x.CourseName?.toLowerCase() === courseName.toLowerCase()
                                    && x.DepartmentName?.toLowerCase() === rowData.DepartmentName?.toLowerCase()
                                    && x.CampusName?.toLowerCase() === rowData.CampusName?.toLowerCase()
                                    && x.CampusAbbreviation?.toLowerCase() == rowData.CampusAbbreviation?.toLowerCase()
                                 )
                            || (
                                    checkOriginalData
                                    && (
                                            x.originalData?.CourseName?.toLowerCase() === courseName.toLowerCase()
                                            && x.originalData?.DepartmentName?.toLowerCase() === rowData.DepartmentName?.toLowerCase()
                                            && x.originalData?.CampusName?.toLowerCase() === rowData.CampusName?.toLowerCase()
                                            && x.originalData?.CampusAbbreviation?.toLowerCase() == rowData.CampusAbbreviation?.toLowerCase()
                                        )
                                )
                    ).length > 1
        },

       isDuplicateCampusAbbreviation(campusAbbreviation, rowData, checkOriginalData){
            var existingCampus = this.isDuplicatedInImportDataCampusAbbreviation(campusAbbreviation, rowData, checkOriginalData);

            if (!existingCampus)
            existingCampus = this.isExistingCampusAbbreviation(campusAbbreviation, rowData);

            return existingCampus;
        },

        isExistingCampusAbbreviation(campusAbbreviation, rowData){
            return campusAbbreviation != ''
                && this.selectListOptions
                    .campuses
                    .some(x => (
                                    x.abbreviation?.toLowerCase() === campusAbbreviation.toLowerCase()
                                    && x.text?.toLowerCase() != rowData.CampusName?.toLowerCase()
                                )
                    )
        },

        isDuplicatedInImportDataCampusAbbreviation(campusAbbreviation, rowData, checkOriginalData){
             //campus Abbreviation should be unique, so if we find a campus with a different name, but a same Abbreviation there is an issue
            return campusAbbreviation != ''
                && this.loadedData
                    .some(x =>
                            (
                                x.CampusAbbreviation.toLowerCase() === campusAbbreviation.toLowerCase()
                                && x.CampusName?.toLowerCase() != rowData.CampusName?.toLowerCase()
                                && x.DepartmentName?.toLowerCase() != rowData.DepartmentName?.toLowerCase()
                                && x.CourseName?.toLowerCase() != rowData.CourseName?.toLowerCase()
                            )
                            ||
                            (
                                checkOriginalData
                                &&  (
                                        x.originalData?.CampusAbbreviation === campusAbbreviation.toLowerCase()
                                        && x.originalData?.CampusName?.toLowerCase() != rowData.CampusName?.toLowerCase()
                                        && x.originalData?.DepartmentName?.toLowerCase() != rowData.DepartmentName?.toLowerCase()
                                        && x.originalData?.CourseName?.toLowerCase() != rowData.CourseName?.toLowerCase()
                                    )
                            )
                        )
        }
    }
};